import React, { useState, useEffect } from "react";
import { Clock, AlertCircle } from "lucide-react";
import { consumirApi } from "../api/ph";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";

export default function GlobalTimer() {
    const location = useLocation();
    const [timeLeft, setTimeLeft] = useState(null);
    const [cargandoRedireccion, setCargandoRedireccion] = useState(() => {
        return !!localStorage.getItem("auto_click_folio");
    });
    const [isLowTime, setIsLowTime] = useState(false);
    const [reservaData, setReservaData] = useState(null);
    const [calendarOpen, setCalendarOpen] = useState(false);

    // Monitorear si el modal del calendario está abierto para ocultar la alerta redundante
    useEffect(() => {
        const checkCalendar = () => {
            const isVisible = !!document.getElementById("modal-calendario-cita");
            if (isVisible !== calendarOpen) {
                setCalendarOpen(isVisible);
            }
        };
        const timer = setInterval(checkCalendar, 250);
        checkCalendar();
        return () => clearInterval(timer);
    }, [calendarOpen]);

    const apiUrl_agendador = process.env.REACT_APP_URL_AGENDADOR;
    const apiKeyCitas = process.env.REACT_APP_API_KEY_CITAS;

    // Obtener código de afiliado de la misma forma que Calendar.jsx
    const userStored = JSON.parse(localStorage.getItem("user") || "{}");
    const rawCodigo = userStored.username || "2023110589";
    const codigoAfiliado = (typeof rawCodigo === "string" && rawCodigo.startsWith("deudo-"))
        ? (rawCodigo.split("-")[1] || rawCodigo)
        : rawCodigo;

    const cancelarCita = async (idCita) => {
        try {
            await consumirApi({
                url: `${apiUrl_agendador}/ApartarCita`,
                method: "POST",
                headers: { 'x-api-key': apiKeyCitas },
                body: {
                    id_cita: idCita,
                    accion: false,
                    codigo_afiliado: codigoAfiliado
                }
            });
        } catch (error) {
            console.error("Error al cancelar la cita automáticamente:", error);
        } finally {
            localStorage.removeItem("reserva_cita");
            window.location.reload();
        }
    };

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    };

    useEffect(() => {
        const actualizarTemporizador = async () => {
            const reservaGuardada = localStorage.getItem("reserva_cita");
            if (!reservaGuardada) {
                if (timeLeft !== null) setTimeLeft(null);
                setReservaData(null);
                return;
            }

            try {
                const parsedReserva = JSON.parse(reservaGuardada);
                setReservaData(parsedReserva);
                const { expiration, id_cita } = parsedReserva;
                const ahora = Date.now();
                const diferencia = expiration - ahora;

                if (diferencia <= 0) {
                    await cancelarCita(id_cita);
                } else {
                    const secondsLeft = Math.ceil(diferencia / 1000);
                    setTimeLeft(secondsLeft);
                    setIsLowTime(secondsLeft < 60);
                }
            } catch (e) {
                console.error("Error parsing reserva_cita:", e);
                localStorage.removeItem("reserva_cita");
                setTimeLeft(null);
                setReservaData(null);
            }
        };

        const intervalo = setInterval(actualizarTemporizador, 1000);
        actualizarTemporizador();

        return () => clearInterval(intervalo);
    }, [apiUrl_agendador, apiKeyCitas, codigoAfiliado, timeLeft]);

    // Auto-clicker effect when redirecting to a page that renders the target table row
    useEffect(() => {
        const autoClickFolio = localStorage.getItem("auto_click_folio");
        if (!autoClickFolio) return;

        const timer = setInterval(() => {
            // Si el modal del calendario ya se renderizó en el DOM, apagar el loader
            if (document.getElementById("modal-calendario-cita")) {
                setCargandoRedireccion(false);
                localStorage.removeItem("auto_click_folio");
                clearInterval(timer);
                return;
            }

            const btn = document.querySelector(`[data-tour="agendar-cita-btn"][data-folio="${autoClickFolio}"]`) ||
                        document.querySelector(`[data-folio="${autoClickFolio}"]`);
            if (btn) {
                btn.scrollIntoView({ behavior: 'smooth', block: 'center' });
                btn.click();
            }
        }, 300);

        const timeout = setTimeout(() => {
            localStorage.removeItem("auto_click_folio");
            setCargandoRedireccion(false);
            clearInterval(timer);
        }, 8000);

        return () => {
            clearInterval(timer);
            clearTimeout(timeout);
        };
    }, [location.pathname]);

    // Background silent token refresh to keep session alive during reservation
    useEffect(() => {
        const renovarTokenSiExpirado = async () => {
            const token = localStorage.getItem("token");
            const refreshToken = localStorage.getItem("refreshToken");
            const apiLoginUrl = process.env.REACT_APP_API_URL_LOGIN;

            if (!token || !refreshToken || !apiLoginUrl) return;

            try {
                // Decodificar el payload del JWT de forma segura
                const parts = token.split('.');
                if (parts.length < 2) return;
                const payload = JSON.parse(atob(parts[1]));
                const exp = payload?.exp;
                if (!exp) return;

                const ahora = Date.now() / 1000;
                // Si expira en menos de 60 segundos, refrescar
                if (exp - ahora < 60) {
                    const response = await fetch(`${apiLoginUrl}/auth/refresh/client`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ refreshToken })
                    });

                    if (response.ok) {
                        const data = await response.json();
                        if (data.ok && data.data?.tokens) {
                            const tokens = data.data.tokens;
                            localStorage.setItem("token", tokens.accessToken);
                            if (tokens.refreshToken) {
                                                    localStorage.setItem("refreshToken", tokens.refreshToken);
                            }
                        }
                    }
                }
            } catch (err) {
                console.error("Error al renovar token en segundo plano:", err);
            }
        };

        // Monitorear cada 30 segundos
        const intervalo = setInterval(renovarTokenSiExpirado, 30000);
        renovarTokenSiExpirado();

        return () => clearInterval(intervalo);
    }, []);

    const handleButtonClick = async () => {
        if (!reservaData?.folioPreregistro) return;

        const targetFolio = String(reservaData.folioPreregistro);
        
        // 1. Intentar buscar el botón de agendar cita (visible o no)
        let btn = document.querySelector(`[data-tour="agendar-cita-btn"][data-folio="${targetFolio}"]`) ||
                  document.querySelector(`[data-folio="${targetFolio}"]`);
        
        if (btn) {
            setCargandoRedireccion(true);
            btn.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setTimeout(() => {
                btn.click();
                
                // Esperar a que el modal del calendario se renderice antes de apagar el loader
                const checkModal = setInterval(() => {
                    if (document.getElementById("modal-calendario-cita")) {
                        setCargandoRedireccion(false);
                        clearInterval(checkModal);
                    }
                }, 100);

                // Timeout de seguridad por si falla la apertura del modal
                setTimeout(() => {
                    setCargandoRedireccion(false);
                    clearInterval(checkModal);
                }, 3000);
            }, 600);
            return;
        }

        // 2. Si no está en el DOM, es porque el usuario está en el menú de inicio u otra ruta.
        // Consultamos la API para buscar a qué tipo de préstamo pertenece este folio.
        const apiUrl = process.env.REACT_APP_API_URL;
        if (!apiUrl) return;

        setCargandoRedireccion(true);
        try {
            const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
            const endpoint = storedUser?.userType?.toUpperCase() === "DEUDO" ? "getTipoPrestamosSinLogin" : "tipoPrestamos";
            
            const catRes = await fetch(`${apiUrl}/${endpoint}`);
            if (!catRes.ok) {
                setCargandoRedireccion(false);
                return;
            }
            const catData = await catRes.json();
            const services = catData?.data?.filter(t => t.es_activo && t.id_catalogo_tramite !== 0 && t.id_catalogo_tramite !== "0" && Number(t.id_tipo_prestamo) !== 7) || [];

            for (const service of services) {
                try {
                    const listRes = await fetch(`${apiUrl}/preregistros/listado/${codigoAfiliado}/${service.nombre}`);
                    if (listRes.ok) {
                        const listData = await listRes.json();
                        const rawList = listData?.data || [];
                        const match = rawList.find(item => String(item.folio) === targetFolio);
                        if (match) {
                            // Guardar datos de navegación para que cargue la vista correcta
                            localStorage.setItem("auto_click_folio", targetFolio);
                            localStorage.setItem("id_tipo_prestamo", service.id_tipo_prestamo);
                            localStorage.setItem("id_catalogo_tramite", service.id_catalogo_tramite);
                            localStorage.removeItem("id_sede");
                            localStorage.setItem("activeView", service.nombre);

                            if (service.nombre.toLowerCase() === "rentas") {
                                window.location.href = "/tramite/Rentas";
                            } else {
                                window.location.href = "/preregistro";
                            }
                            return;
                        }
                    }
                } catch (err) {
                    console.error("Error al buscar listado para servicio:", service.nombre, err);
                }
            }
        } catch (e) {
            console.error("Error al redirigir al trámite correcto:", e);
        } finally {
            setTimeout(() => {
                setCargandoRedireccion(false);
            }, 1500);
        }
    };

    if (timeLeft === null) return null;

    return (
        <>
            <AnimatePresence>
                {(timeLeft !== null && !calendarOpen) && (
                    <motion.div
                        initial={{ y: -100, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: -100, opacity: 0 }}
                        /* En móvil ocupa casi todo el ancho, centrado con margins; en desktop se limita */
                        className="fixed top-3 sm:top-6 left-3 right-3 mx-auto z-[10000] max-w-sm sm:max-w-md"
                    >
                        <div className={`
                            relative overflow-hidden
                            bg-white/95 backdrop-blur-xl
                            border-2 ${isLowTime ? 'border-red-500/50' : 'border-primary/20'}
                            shadow-[0_16px_40px_-10px_rgba(0,0,0,0.18)]
                            rounded-2xl sm:rounded-3xl
                            flex flex-col
                        `}>
                            {/* Barra de progreso superior */}
                            <div
                                className={`h-1 w-full bg-gradient-to-r ${isLowTime ? 'from-red-500 to-orange-500' : 'from-primary to-indigo-500'} transition-all duration-1000`}
                            />

                            {/* Contenido principal */}
                            <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-6 sm:py-4">

                                {/* Icono + Texto */}
                                <div className="flex items-center gap-3 min-w-0">
                                    {/* Icono — más pequeño en móvil */}
                                    <div className={`
                                        flex-shrink-0 flex items-center justify-center
                                        size-9 sm:size-11 rounded-xl sm:rounded-2xl
                                        ${isLowTime ? 'bg-red-50 text-red-500' : 'bg-primary/10 text-primary'}
                                        transition-colors duration-500
                                    `}>
                                        {isLowTime ? (
                                            <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 animate-pulse" />
                                        ) : (
                                            <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
                                        )}
                                    </div>

                                    {/* Texto */}
                                    <div className="min-w-0">
                                        <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-[0.15em] text-gray-400 leading-none mb-0.5 truncate">
                                            Reserva Temporal
                                        </p>
                                        <h4 className="text-xs sm:text-sm font-bold text-gray-800 leading-tight truncate">
                                            {isLowTime ? '¡Tiempo casi agotado!' : 'Agendando cita'}
                                        </h4>
                                        {reservaData?.folioPreregistro && (
                                            <p className="text-[9px] sm:text-[10px] font-black text-primary/80 uppercase tracking-wider animate-pulse mt-0.5 truncate">
                                                Folio: {reservaData.folioPreregistro}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Contador */}
                                <div className="flex-shrink-0 flex flex-col items-end">
                                    <span className={`
                                        text-2xl sm:text-3xl font-black tabular-nums tracking-tighter leading-none
                                        ${isLowTime ? 'text-red-600' : 'text-primary'}
                                        transition-colors duration-500
                                    `}>
                                        {formatTime(timeLeft)}
                                    </span>
                                    <div className="flex gap-1 mt-1.5">
                                        {[1, 2, 3].map((i) => (
                                            <div
                                                key={i}
                                                className={`size-1 rounded-full ${isLowTime ? 'bg-red-400 animate-ping' : 'bg-primary/30'}`}
                                                style={{ animationDelay: `${i * 0.2}s` }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Botón de acción interactivo */}
                            {reservaData?.folioPreregistro && (
                                <button
                                    onClick={handleButtonClick}
                                    className={`
                                        w-full border-t py-2.5 px-4
                                        font-bold text-xs uppercase tracking-wider text-center
                                        flex items-center justify-center gap-2
                                        transition-all duration-200 active:scale-95
                                        rounded-b-2xl sm:rounded-b-3xl
                                        ${isLowTime 
                                            ? 'bg-red-500 hover:bg-red-600 text-white border-red-600/20' 
                                            : 'bg-amber-500 hover:bg-amber-600 text-white border-amber-600/20'
                                        }
                                    `}
                                >
                                    <Clock className="w-4 h-4 animate-pulse" />
                                    <span>Continuar Cita</span>
                                </button>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Redirection Loader */}
            {cargandoRedireccion && (
                <div className="fixed inset-0 z-[99999] flex flex-col items-center justify-center bg-slate-950/45 backdrop-blur-md">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 15 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        className="bg-white/90 p-8 rounded-3xl shadow-2xl flex flex-col items-center max-w-xs text-center border border-slate-200/20"
                    >
                        <div className="relative size-16 mb-4 flex items-center justify-center">
                            {/* Outer spinner */}
                            <div className="absolute inset-0 rounded-full border-4 border-primary/10 border-t-primary animate-spin" />
                            {/* Inner icon */}
                            <Clock className="size-6 text-primary animate-pulse" />
                        </div>
                        <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest mb-1.5">
                            Redirigiendo
                        </h3>
                        <p className="text-[11px] text-slate-500 font-bold tracking-tight">
                            Buscando tu trámite para continuar con tu cita...
                        </p>
                    </motion.div>
                </div>
            )}
        </>
    );
}
